import { LinearProgress } from '@mui/material'
import React, { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router'
import { Redirect } from '@/helpers/Redirect'
import { useAuthContext } from '@/providers/Global'
import { landingRoute } from '@/utils/authorities'
import { LayoutIndex } from './_layout/Index'
import { AccountingRouter } from './accounting/_router'
import { AnalyticsRouter } from './analytics/_router'
import { ChangelogRouter } from './changelog/_router'
import { CommunicationsRouter } from './communications/_router'
import { ComplianceRouter } from './compliance/_router'
import { CrewRouter } from './crew/_router'
import { DashboardRouter } from './dashboard/_router'
import { DevRouter } from './dev/_router'
import { DocumentationRouter } from './documentation/_router'
import { EntitiesRouter } from './entities/_router'
import { HumanResourcesRouter } from './human-resources/_router'
import { MaintenanceRouter } from './maintenance/_router'
import { MediaCatalogRouter } from './media-catalog/_router'
import { MembersRouter } from './members/_router'
import { OperationsRouter } from './operations/_router'
import { SafetyRouter } from './safety/_router'
import { SafetyReportRouter } from './safety/report/_router'
import { SettingsRouter } from './settings/_router'
import { TodosRouter } from './todos/_router'
import { ToolsRouter } from './tools/_router'
import { TrainingRouter } from './training/_router'

const E404Index = lazy(() => import('@/routes/_404/Index'))
const AuthLoginIndex = lazy(() => import('@/routes/_auth/login/Index'))
const AuthForgotPasswordIndex = lazy(() => import('@/routes/_auth/forgot-password/Index'))
const AuthResetPasswordIndex = lazy(() => import('@/routes/_auth/reset-password/Index'))
const AuthInviteIndex = lazy(() => import('@/routes/_auth/invite/Index'))
const SafetyExternalIndex = lazy(() => import('@/routes/_auth/safety-external/Index'))
const KioskTimeIndex = lazy(() => import('@/routes/_auth/kiosk-time/Index'))

// Collaborateur ou membre : tableau de bord ; compte externe (client CAMO, NAA) : sa première entrée de menu.
const LandingRedirect = () => {
  const Auth = useAuthContext()

  return <Redirect to={landingRoute(Auth.active?.roles, Auth.active?.user?.roles)} />
}

export const Router = React.memo(() => (
  <Suspense fallback={<LinearProgress />}>
    <Routes>
      <Route path='login' element={<AuthLoginIndex />} />
      <Route path='forgot-password' element={<AuthForgotPasswordIndex />} />
      <Route path='reset-password' element={<AuthResetPasswordIndex />} />
      <Route path='invite' element={<AuthInviteIndex />} />
      {/* Formulaire public de dépôt des tiers (CAMO.A.202(d)) : hors LayoutIndex, sans session. */}
      <Route path='safety/external/:token' element={<SafetyExternalIndex />} />
      {/* Kiosque de pointage : hors LayoutIndex, l'appareil s'identifie par son jeton, la personne par code et PIN.
          Route parente à un segment : extractSpaRoots n'en retient pas d'autres pour les liens directs. */}
      <Route path='kiosk'>
        <Route path='time' element={<KioskTimeIndex />} />
      </Route>

      <Route element={<LayoutIndex />}>
        <Route path='*' element={<E404Index />} />

        <Route index element={<LandingRedirect />} />

        <Route path='changelog' element={<ChangelogRouter />} />

        <Route path='dashboard/*' element={<DashboardRouter />} />

        <Route path='entities/*' element={<EntitiesRouter />} />
        <Route path='accounting/*' element={<AccountingRouter />} />
        <Route path='human-resources/*' element={<HumanResourcesRouter />} />
        <Route path='members/*' element={<MembersRouter />} />
        {/* Catalogue média : URL sous /communications (fil d'Ariane « Communications » cliquable +
            navigation Media ↔ Campaigns), mais monté ici au niveau racine — PAS dans
            CommunicationsRouter — pour rester accessible à TOUT utilisateur connecté (le SubRouter
            communications est gated par role--communications). La route statique plus spécifique
            prime sur communications/* (ranking React Router v6, indépendant de l'ordre). */}
        <Route path='communications/media-catalog/*' element={<MediaCatalogRouter />} />
        <Route path='communications/*' element={<CommunicationsRouter />} />
        <Route path='operations/*' element={<OperationsRouter />} />
        {/* Compliance : route de PREMIER NIVEAU (et non sous /operations) — le module couvre
            l'AOC, la CAMO et le transverse, aucun de ces périmètres n'appartient aux ops. */}
        <Route path='compliance/*' element={<ComplianceRouter />} />
        {/* Déposer un rapport est ouvert à TOUT utilisateur connecté (SMM, décision G) : monté
            ici au niveau racine, PAS dans SafetyRouter qui est gardé par role--safety. La route
            plus spécifique prime (ranking React Router v6), comme communications/media-catalog. */}
        <Route path='safety/report/*' element={<SafetyReportRouter />} />
        <Route path='safety/*' element={<SafetyRouter />} />
        <Route path='maintenance/*' element={<MaintenanceRouter />} />
        <Route path='training/*' element={<TrainingRouter />} />
        <Route path='analytics/*' element={<AnalyticsRouter />} />
        <Route path='settings/*' element={<SettingsRouter />} />

        <Route path='tools/*' element={<ToolsRouter />} />
        <Route path='documentation/*' element={<DocumentationRouter />} />
        <Route path='todos/*' element={<TodosRouter />} />
        {/* Liens universels de MyEAPC Crew (QR du stock) : repli quand le lien s'ouvre dans un navigateur. */}
        <Route path='crew/*' element={<CrewRouter />} />
        <Route path='dev/*' element={<DevRouter />} />
      </Route>
    </Routes>
  </Suspense>
))
